import React, { FC, useMemo } from 'react'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { useSelector } from 'react-redux'
import { questionsSelectors } from '@ucheba/store/questions'
import { plannerProgramsSelectors } from '@ucheba/store/planner/programs'
import { profileSelectors } from '@ucheba/store/profile'
import styled, { StyledProps } from 'styled-components'
import { IProgramIdPageButtons } from '../types'
import WantToStudy from '../../../../../../components/LayoutInstitution/components/WantToStudy/desktop'
import OfficialRepresentative from '../../../../../../components/LayoutInstitution/components/OfficialRepresentative/desktop'
import { useWantToStudy } from '../../../../../../components/LayoutInstitution/components/WantToStudy/bll/useWantToStudy'
import { EFormType } from '../../../../../../components/UsersCommunication/types'
import CommunicationForm from '../../../../../../components/UsersCommunication/desktop/CommunicationForm'
import { useProgramsOfficialRepresentative } from '../bll/useProgramsOfficialRepresentative'
import OddsAssessment from '../../../../../../components/OddsAssessment/desktop'
import { useProgramExams } from '../bll/useProgramExams'
import { hasBudgetPlaces } from '../bll/hasBudgetPlaces'
import { usePaidPackage } from '../bll/usePaidPackage'
import EducationalInstitutionAddedNotification from '../../../../../../components/LayoutInstitution/components/WantToStudy/components/EducationalInstitutionAddedNotification/desktop'
import AdmissionInstructionButton from '../../../../../uz/components/AdmissionInstructionButton/desktop'

const StickyContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const StickyTop = styled.div<StyledProps<any>>`
  position: sticky;
  top: 16px;
  padding-bottom: ${(props) => props.paddingBottom || 0};
`

const StickyBottom = styled.div`
  position: sticky;
  bottom: 0;
`

const ProgramIdPageButtons: FC<IProgramIdPageButtons> = (props) => {
  const { program, institution, faculty } = props
  const questions = useSelector(questionsSelectors.entity)
  const programIds = useSelector(plannerProgramsSelectors.entityProgramsIds)
  const profile = useSelector(profileSelectors.entity)
  const { requiredExams: requiredSubjects, optionalExams: optionalSubjects } = program
  const achievements = profile?.enrolleeInfo?.achievements || []
  const achievementIds = achievements.map((el) => el.id)
  const hasPaidPackage = usePaidPackage()

  const institutionId = useMemo(
    () => program?.faculty?.id || program?.institution?.id,
    [program]
  )

  const hasAdmissionInstruction = useMemo(
    () =>
      !!(
        program?.faculty?.admissionInstruction ||
        program?.institution?.admissionInstruction
      ),
    [program]
  )

  const { requiredExams, optionalExams, selectedExams } = useProgramExams(
    requiredSubjects,
    optionalSubjects
  )

  const {
    openProgramAdded,
    handleToggleProgramAdded,
    isInPlanOfAdmission,
    handleWantToStudy,
  } = useWantToStudy({
    id: program.id,
    ids: programIds?.entityIds || [],
    type: 'program',
  })

  const { isOpen, handleOpenDialog, onClose, scrollToBlock } =
    useProgramsOfficialRepresentative()

  const { officialRepresentative, programType } = program

  // есть ли бюджетные места
  const hasBudget = hasBudgetPlaces(program.trainingForms)

  return (
    <>
      <StickyContainer>
        <StickyTop
          paddingBottom={
            program && officialRepresentative && hasPaidPackage ? `332px` : 0
          }
        >
          <WantToStudy
            buttonText='Хочу поступить'
            isShortText
            isInPlanOfAdmission={isInPlanOfAdmission}
            handleWantToStudy={handleWantToStudy}
            size='small'
            type='program'
            programId={program.id}
            programType={programType.value}
            block
            isShowingHint
          />

          <Spacing spacing='small' />

          {requiredExams.length > 0 && hasBudget && (
            <>
              <OddsAssessment
                programId={program.id}
                requiredExams={requiredExams}
                optionalExams={optionalExams}
                selectedExams={selectedExams}
                programName={program.name}
                programIds={programIds.entityIds}
                achievements={achievementIds}
                institutionsName={
                  faculty ? faculty.name || institution?.name : institution?.name || ''
                }
              >
                Оценить шансы
              </OddsAssessment>

              <Spacing spacing='small' />
            </>
          )}

          {hasAdmissionInstruction && institutionId && (
            <div>
              <Spacing spacing='small' />

              <AdmissionInstructionButton institutionId={institutionId} size='small' />
            </div>
          )}
        </StickyTop>

        <StickyBottom>
          <Spacing spacing='small' />

          {program && officialRepresentative && hasPaidPackage && (
            <OfficialRepresentative
              fullName={officialRepresentative.fullName}
              avatar={officialRepresentative.avatar}
              type={institution?.type.value}
              handleOpenDialog={handleOpenDialog}
              size='small'
            />
          )}

          <Spacing spacing='small' />
        </StickyBottom>
      </StickyContainer>

      <CommunicationForm
        parentId={null}
        onClose={onClose}
        formType={EFormType.questions}
        isOpen={isOpen}
        scrollTo={scrollToBlock}
        list={questions?.items || []}
        rootId={program.id}
        type='program'
      />

      <EducationalInstitutionAddedNotification
        lsKey='programInstitutionAddedNotification'
        open={openProgramAdded}
        onClose={handleToggleProgramAdded}
        title='Программа добавлена <br/> в план поступления'
      />
    </>
  )
}

export default ProgramIdPageButtons
